import React, { useState, useEffect, useCallback, useRef } from "react"
import { useIntl } from "react-intl"
import {
  FormAutocomplete,
  FormSelectOption,
  useWatch,
  useFormContext,
  FormTextField,
  FormRadioGroup
} from "@carrotfertility/carotene-core-x"
import { Box, Progress, Stack } from "@carrotfertility/carotene-core"
import { ExtendedProvidersTypeaheadProvider } from "#/pages/provider-finder/types/Providers"
import {
  useDebouncedSearch,
  renderAutocompleteProviderOption
} from "#/pages/provider-finder/components/keyword-search/keywordSearchUtils"
import { CountryCode } from "#/utils/CountryCode"
import { FormValues, extractStringValue, PROVIDER_CONSTANTS } from "../../utils/providerSelectionUtils"
import { useGetExtendedProvidersTypeahead } from "#/pages/provider-finder/hooks/useGetExtendedProvidersTypeahead"

// ============ Props Types ============

interface ProviderSearchFormProps {
  /** User's country code for filtering search results */
  countryCode: CountryCode
  /** Callback when user types in the provider name field (for mutual exclusivity) */
  onProviderNameChange?: (value: string) => void
}

// ============ ProviderSearchForm ============

/**
 * Provider search form component with autocomplete functionality.
 * Reusable base component for both with and without "no provider" option.
 *
 * Key responsibilities:
 * - Manages autocomplete search with debouncing
 * - Syncs form field value with hidden provider object field
 * - Handles external field resets (e.g., when "no provider" is selected)
 * - Provides callback to parent when user types (for mutual exclusivity)
 */
export function ProviderSearchForm({ countryCode, onProviderNameChange }: ProviderSearchFormProps): JSX.Element {
  const intl = useIntl()
  const [openOptionsMenu, setOpenOptionsMenu] = useState<boolean>(false)
  const providerNameValue: FormSelectOption | string | null = useWatch({ name: "providerName" })
  const { setValue, resetField } = useFormContext<FormValues>()

  // Track previous value to detect external clears
  const prevProviderNameValue = useRef<string | null>(null)
  // True once the user has typed in the input. Stays false on initial mount so defaultValues.provider
  // (uuid/eligibility from re-entry) is not cleared before the user has interacted.
  const hasUserInteracted = useRef(false)

  const {
    inputValue,
    loading: isTyping,
    setInputValue,
    searchValue
  } = useDebouncedSearch((value) => {
    setValue("providerName", { value, label: value }, { shouldDirty: true, shouldTouch: true })
    if (value?.trim()) {
      onProviderNameChange?.(value)
    }
  }, setOpenOptionsMenu)

  // Handle external clears (e.g., when "no provider" is selected)
  useEffect(() => {
    const nameStr = extractStringValue(providerNameValue) || ""
    const prevNameStr = prevProviderNameValue.current

    if (!nameStr && prevNameStr) {
      resetField("providerName", { defaultValue: "" })
    }

    prevProviderNameValue.current = nameStr
  }, [providerNameValue, resetField])

  // Fetch provider search results (only filter by country for US users)
  const countryCodeForQuery = countryCode === "US" ? countryCode : null
  const providerQuery = useGetExtendedProvidersTypeahead(
    searchValue ?? "",
    PROVIDER_CONSTANTS.FERTILITY_CLINIC_PARENT_ID,
    countryCodeForQuery
  )

  const isSearchLoading = isTyping || providerQuery.isLoading

  /**
   * Finds the matching provider object from search results.
   * Normalizes whitespace to handle spacing differences.
   */
  const getProviderFromSelectOption = useCallback(
    (option: FormSelectOption | string | null): ExtendedProvidersTypeaheadProvider | null => {
      if (!option || !providerQuery.data) return null

      const value = extractStringValue(option) || ""
      if (!value) return null

      return (
        providerQuery.data.find((provider) => provider?.name.replace(/\s/g, "") === value.replace(/\s/g, "")) ?? null
      )
    },
    [providerQuery.data]
  )

  // Sync hidden "provider" field with "providerName" field
  useEffect(() => {
    const setValueOptions = { shouldDirty: true, shouldTouch: true }
    const nameIsEmpty = !providerNameValue || extractStringValue(providerNameValue) === ""

    if (nameIsEmpty) {
      setValue("provider", null, setValueOptions)
      return
    }

    const provider = getProviderFromSelectOption(providerNameValue)
    if (provider) {
      setValue("providerName", { value: provider.name, label: provider.name }, setValueOptions)
      setValue("provider", provider, setValueOptions)
    } else if (hasUserInteracted.current) {
      // User has typed in the input and the name doesn't match any search result —
      // treat as freeform entry and clear the structured provider object.
      setValue("provider", null, setValueOptions)
    }
    // If the user hasn't interacted yet, don't touch provider — it may have been initialised
    // from defaultValues (e.g. re-entry after going back when a typeahead provider was
    // previously saved). Clearing here would lose the uuid and eligibility status.
  }, [providerNameValue, setValue, getProviderFromSelectOption])

  return (
    <>
      <FormAutocomplete
        name="providerName"
        label={intl.formatMessage({ defaultMessage: "Clinic name" })}
        options={
          isSearchLoading || providerQuery.isError
            ? []
            : providerQuery.data?.map((provider) => ({
                value: provider.name,
                label: provider.name
              })) || []
        }
        freeSolo
        loading={isSearchLoading}
        loadingText={<LoadingIndicator />}
        onInputChange={(_event, newInputValue, reason) => {
          if (reason !== "reset") {
            hasUserInteracted.current = true
            setInputValue(newInputValue)
          }
        }}
        renderOption={(props, option) => {
          const provider = getProviderFromSelectOption(option)
          return renderAutocompleteProviderOption(props, provider, inputValue)
        }}
        open={openOptionsMenu}
        onClose={() => setOpenOptionsMenu(false)}
        popupIcon={null}
        fullWidth
        controllerProps={{ rules: {} }}
      />
      {/* Hidden field to store the full provider object with UUID */}
      <FormTextField name="provider" sx={{ display: "none" }} aria-hidden="true" />
    </>
  )
}

// ============ ProviderSearchFormWithNoProviderOption ============

/**
 * Provider search form with "I don't have a provider yet" radio option.
 *
 * Handles mutual exclusivity between provider name input and "no provider" selection:
 * - When user types in provider name → clears "no provider" selection
 * - When user selects "no provider" → clears provider name and provider fields
 */
export function ProviderSearchFormWithNoProviderOption({ countryCode }: { countryCode: CountryCode }): JSX.Element {
  const intl = useIntl()
  const { setValue } = useFormContext<FormValues>()

  const handleProviderNameChange = useCallback(
    (_value: string) => {
      setValue("noProvider", "", { shouldDirty: true })
    },
    [setValue]
  )

  const handleNoProviderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === PROVIDER_CONSTANTS.NO_PROVIDER_RADIO_VALUE) {
        setValue("providerName", "", { shouldDirty: true })
        setValue("provider", null, { shouldDirty: true })
      }
    },
    [setValue]
  )

  const noProviderOptions = [
    {
      value: PROVIDER_CONSTANTS.NO_PROVIDER_RADIO_VALUE,
      label: intl.formatMessage({ defaultMessage: "I don't have a provider yet" })
    }
  ]

  return (
    <Stack gap={(theme) => theme.spacing(theme.tokens.spacing.md)}>
      <ProviderSearchForm countryCode={countryCode} onProviderNameChange={handleProviderNameChange} />
      <FormRadioGroup
        name="noProvider"
        options={noProviderOptions}
        radioGroupProps={{ onChange: handleNoProviderChange }}
        controllerProps={{ rules: {} }}
      />
    </Stack>
  )
}

// ============ Helper Components ============

/**
 * Loading indicator for autocomplete dropdown.
 */
function LoadingIndicator(): JSX.Element {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        inlineSize: "100%",
        padding: theme.spacing(theme.tokens.spacing.xxl),
        flexGrow: 1
      })}
    >
      <Progress />
    </Box>
  )
}
